import { Box, useMediaQuery, useTheme } from '@mui/material';
import * as React from 'react';
import { useState, useCallback, useImperativeHandle, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import {
  VehiclePicture,
  VehiclePictureTab,
  VEHICLE_FIELDS,
  TAB_VISIBILITY_CLIENT,
  TAB_VISIBILITY_WAREHOUSE,
  TAB_VISIBILITY_PUBLIC,
} from 'types/vehicles/types';
import { vehiclePageStyles } from '../styles';
import axiosService from 'utils/axios';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/captions.css';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Download from 'yet-another-react-lightbox/plugins/download';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import { ErrorResponse } from 'types/general/types';
import { UploadProgress } from '../hooks/use_file_upload';
import '../styles.css';

// Hooks
import { usePictureSelection } from './hooks/usePictureSelection';
import { usePictureOperations } from './hooks/usePictureOperations';

// Components
import { PictureGrid } from './components/PictureGrid';
import { PictureActions } from './components/PictureActions';
import { DeleteConfirmDialog } from './components/DeleteConfirmDialog';
import { TabsManager } from './components/TabsManager';

// Utils
import {
  PictureToDisplay,
  parsePicturesToDisplay,
  getIndicesToRemove,
  calculatePagination,
} from './utils/pictureUtils';

// Default tabs for new vehicles
const DEFAULT_TABS: VehiclePictureTab[] = [
  {
    id: 'pictures',
    name: 'Pictures',
    visibility: [TAB_VISIBILITY_CLIENT, TAB_VISIBILITY_WAREHOUSE, TAB_VISIBILITY_PUBLIC],
    // eslint-disable-next-line camelcase
    is_default: true,
  },
];

interface VehiclePicturesSectionProps {
  vehicleId?: string;
  initialPictures?: VehiclePicture[];
  initialTabs?: VehiclePictureTab[];
  onError?: (error: ErrorResponse | null) => void;
  setIsLoading?: (loading: boolean) => void;
  onUploadProgressChange?: (progress: UploadProgress) => void;
  canEdit: (fieldName: string) => boolean;
  onVehicleDataRefresh?: () => void;
}

export interface VehiclePicturesSectionRef {
  uploadNewVehiclePictures: (vehicleId: string) => Promise<void>;
  getTemporaryPictureFiles: () => File[];
}

const VehiclePicturesSection = React.forwardRef(
  (
    {
      vehicleId,
      initialPictures = [],
      initialTabs = [],
      onError,
      setIsLoading,
      onUploadProgressChange,
      canEdit,
      onVehicleDataRefresh,
    }: VehiclePicturesSectionProps,
    ref
  ) => {
    const { id } = useParams();
    const addingNewVehicle = id === 'new';

    const user = useSelector((state: RootState) => state.auth.user);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const canEditPictures = canEdit(VEHICLE_FIELDS.PICTURES);

    // State
    const [vehiclePicturesToDisplay, setVehiclePicturesToDisplay] = useState<PictureToDisplay[]>([]);
    const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
    const [currentLightboxIndex, setCurrentLightboxIndex] = useState(0);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [tabs, setTabs] = useState<VehiclePictureTab[]>(addingNewVehicle ? DEFAULT_TABS : initialTabs);
    const [selectedTabId, setSelectedTabId] = useState<string>(
      addingNewVehicle ? DEFAULT_TABS[0].id : initialTabs[0]?.id || ''
    );

    /**
     * Resolves false when the refresh failed, so a caller with its own message to show (a partial
     * delete) can keep both instead of one replacing the other.
     */
    const updateVehiclePictures = useCallback((): Promise<boolean> => {
      if (vehicleId && !addingNewVehicle) {
        setIsLoading?.(true);
        return axiosService
          .get(`/vehicles/vehicle/${vehicleId}/`)
          .then(res => {
            setVehiclePicturesToDisplay(parsePicturesToDisplay(res.data.pictures));
            // Update tabs from backend
            if (res.data.tabs && res.data.tabs.length > 0) {
              setTabs(res.data.tabs);
              // If current selected tab is no longer valid, select the first tab
              setSelectedTabId(currentId => {
                if (!res.data.tabs.find((t: VehiclePictureTab) => t.id === currentId)) {
                  return res.data.tabs[0].id;
                }
                return currentId;
              });
            }
            // Refresh full vehicle data from parent to update other fields that might have changed
            onVehicleDataRefresh?.();
            return true;
          })
          .catch(() => {
            onError?.({ status: '', statusText: 'Error', message: 'Failed to fetch vehicle pictures.' });
            return false;
          })
          .finally(() => setIsLoading?.(false));
      }
      return Promise.resolve(true);
    }, [vehicleId, addingNewVehicle, onError, setIsLoading, onVehicleDataRefresh]);

    const {
      addPictures,
      setAsCover,
      deletePictures,
      uploadTemporaryPictures,
      uploadProgress,
      deleteProgress,
      temporaryPictureFiles,
      temporaryDisplayPictures,
    } = usePictureOperations({
      vehicleId,
      addingNewVehicle,
      userId: user?.id,
      selectedTabId,
      onError,
      setIsLoading,
      onSuccess: updateVehiclePictures,
    });

    // Use temporary display pictures for new vehicles, otherwise use fetched pictures
    const allDisplayPictures = addingNewVehicle ? temporaryDisplayPictures : vehiclePicturesToDisplay;

    // Filter pictures by selected tab
    const displayPictures = allDisplayPictures.filter(picture => {
      // For new vehicles with default tabs, show all pictures in all tabs for now
      // Backend will assign proper tabs when vehicle is created
      if (addingNewVehicle) {
        return true;
      }
      // For existing vehicles, filter by tab ID
      return picture.tab === selectedTabId;
    });

    // Custom hooks
    const { selectedPictureIds, togglePictureSelection, selectAll, clearSelection, isSelectedPictureAlreadyCover } =
      usePictureSelection(displayPictures);

    // Picture upload handler
    const handlePictureInput = useCallback(
      async (e: React.ChangeEvent<HTMLInputElement>) => {
        const { files } = e.target;
        if (files && files.length > 0) {
          const filesArr = [...files];
          // Clear input for reuse
          e.target.value = '';

          addPictures(filesArr);
        }
      },
      [addPictures]
    );

    // Set as cover picture handler
    const handleSetAsCover = useCallback(() => {
      if (selectedPictureIds.size !== 1) return;

      const [pictureId] = Array.from(selectedPictureIds);
      const selectedPictureIndex = displayPictures.findIndex(pic => pic.id === pictureId);
      if (selectedPictureIndex === -1) return;

      setAsCover(selectedPictureIndex, displayPictures).then(() => {
        clearSelection();
        setCurrentPage(1);
      });
    }, [selectedPictureIds, displayPictures, setAsCover, clearSelection]);

    // Delete pictures handler
    const handleDeleteConfirm = useCallback(async () => {
      if (selectedPictureIds.size === 0) {
        setShowDeleteConfirmation(false);
        return;
      }

      setShowDeleteConfirmation(false);

      const pictureIdsSet = new Set(selectedPictureIds);
      const indicesToRemove = getIndicesToRemove(displayPictures, pictureIdsSet);

      // Pictures that failed to delete stay selected, so retrying them is one click
      deletePictures(indicesToRemove, displayPictures).then(failedIds => {
        clearSelection();
        selectAll(failedIds);
      });
    }, [selectedPictureIds, displayPictures, deletePictures, clearSelection, selectAll]);

    // Tab change handler
    const handleTabChange = useCallback(
      (tabId: string) => {
        setSelectedTabId(tabId);
        clearSelection();
        setCurrentPage(1);
      },
      [clearSelection]
    );

    // Initialize pictures on mount
    useEffect(() => {
      if (initialPictures.length > 0) {
        setVehiclePicturesToDisplay(parsePicturesToDisplay(initialPictures));
      }
    }, [initialPictures]);

    // Sync tabs with prop changes from parent (only for initial load)
    useEffect(() => {
      if (!addingNewVehicle && initialTabs.length > 0 && tabs.length === 0) {
        setTabs(initialTabs);
        setSelectedTabId(initialTabs[0]?.id || '');
      }
    }, [initialTabs, addingNewVehicle, tabs.length]);

    // Notify parent of upload or delete progress changes
    useEffect(() => {
      if (onUploadProgressChange) {
        onUploadProgressChange(deleteProgress.isUploading ? deleteProgress : uploadProgress);
      }
    }, [uploadProgress, deleteProgress, onUploadProgressChange]);

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      uploadNewVehiclePictures: async (newVehicleId: string) => {
        await uploadTemporaryPictures(newVehicleId);
      },
      getTemporaryPictureFiles: () => temporaryPictureFiles,
    }));

    // Calculate pagination
    const picturesPerPage = isMobile ? 7 : 13;
    const { totalPages, startIndex, endIndex } = calculatePagination(
      displayPictures.length,
      picturesPerPage,
      currentPage
    );
    const paginatedPictures = displayPictures.slice(startIndex, endIndex);

    // Reset to page 1 if current page exceeds total pages
    useEffect(() => {
      if (currentPage > totalPages && totalPages > 0) {
        setCurrentPage(1);
      }
    }, [currentPage, totalPages]);

    const isSelectedAlreadyCover = isSelectedPictureAlreadyCover();

    // Select every picture in the current tab, across all pages, so "select all, delete" empties the tab
    const handleSelectAll = useCallback(() => {
      selectAll(displayPictures.map(pic => pic.id));
    }, [displayPictures, selectAll]);

    const allTabPicturesSelected =
      displayPictures.length > 0 && displayPictures.every(pic => selectedPictureIds.has(pic.id));

    return (
      <>
        <Box sx={vehiclePageStyles.card}>
          <Box sx={vehiclePageStyles.cardHeader}>Vehicle Imagery</Box>

          {/* Tabs for organizing pictures */}
          <TabsManager
            vehicleId={vehicleId}
            addingNewVehicle={addingNewVehicle}
            canEdit={canEditPictures}
            tabs={tabs}
            selectedTabId={selectedTabId}
            allDisplayPictures={allDisplayPictures}
            onTabChange={handleTabChange}
            onPageChange={setCurrentPage}
            onError={onError}
            setIsLoading={setIsLoading}
            onSuccess={updateVehiclePictures}
          />

          <PictureGrid
            paginatedPictures={paginatedPictures}
            selectedPictureIds={selectedPictureIds}
            startIndex={startIndex}
            currentPage={currentPage}
            totalPages={totalPages}
            isMobile={isMobile}
            canEditPictures={canEditPictures}
            onPictureClick={index => {
              setCurrentLightboxIndex(index);
              setIsImageViewerOpen(true);
            }}
            onToggleSelection={togglePictureSelection}
            onPageChange={setCurrentPage}
            onAddPictures={handlePictureInput}
          />

          {/* Footer with action buttons */}
          {canEditPictures ? (
            <PictureActions
              selectedCount={selectedPictureIds.size}
              allSelected={allTabPicturesSelected}
              isSelectedAlreadyCover={isSelectedAlreadyCover}
              onAddPictures={handlePictureInput}
              onSelectAll={handleSelectAll}
              onSetAsCover={handleSetAsCover}
              onDelete={() => setShowDeleteConfirmation(true)}
            />
          ) : null}
        </Box>

        {/* Image Viewer */}
        <Lightbox
          open={isImageViewerOpen}
          close={() => setIsImageViewerOpen(false)}
          slides={displayPictures}
          index={currentLightboxIndex}
          on={{ view: ({ index }) => setCurrentLightboxIndex(index) }}
          plugins={[Captions, Zoom, Download, Thumbnails, Fullscreen]}
          carousel={{ finite: true }}
        />

        {/* Delete Picture Confirmation Dialog */}
        <DeleteConfirmDialog
          open={showDeleteConfirmation}
          title="Delete selected pictures"
          message={`Are you sure you want to delete ${selectedPictureIds.size} picture${
            selectedPictureIds.size > 1 ? 's' : ''
          }?`}
          onClose={() => setShowDeleteConfirmation(false)}
          onConfirm={handleDeleteConfirm}
        />
      </>
    );
  }
);

VehiclePicturesSection.displayName = 'VehiclePicturesSection';

export default VehiclePicturesSection;
